import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import {
  Campaign,
  formatCampaignPlatformList,
  getCampaignPlatformIds,
} from '../../services/campaign.service';
import {
  CampaignPlatformOverviewRow,
  buildCampaignPlatformOverviewRows,
} from '../campaign-editor/optional-setting/campaign-platform-url.util';
import {
  LANDING_PAGE_PENDING_SHARE_LINKS_HINT,
  isCampaignLandingPageReady,
} from '../campaign-editor/optional-setting/common-website-content.mapper';

/**
 * Displays a concise overview of a Campaign, including location information, user counts, formatted dates, and quick actions to edit or view analytics.
 * The component is read-only (receives campaign data via @Input) and performs navigation using the injected Router for edit and analytics actions.
 * Key methods:
 *   - getLocationTooltipText(): Builds a tooltip string from campaign.location_list (bullet list) or falls back to campaign.location.
 *   - getUserCount(): Returns campaign.total_customers or 0 as a fallback.
 *   - formatDate(dateString): Formats an ISO date string into a human-friendly date (e.g., "January 1, 2020") using toLocaleDateString.
 *   - onEditClick(): Navigates to the campaign editor overview route with campaignId as a query parameter.
 *   - onViewAnalytics(): Navigates to the dashboard with campaignId and a tab query parameter for Campaign Management.
 * Important lifecycle notes:
 *   - No Angular lifecycle hooks (e.g., ngOnInit) are implemented; the component expects the campaign @Input to be provided and up-to-date by the parent.
 * @component
 * @selector app-campaign-overview
 * @example
 *   <app-campaign-overview [campaign]="sampleCampaign"></app-campaign-overview>
 * @input Campaign campaign - The campaign object to display; expected fields include id, location, location_list, total_customers, and other metadata used by the overview.
 * @output none none - This component does not emit any output events; interactions are handled by navigating via the Router.
 */
@Component({
  selector: 'app-campaign-overview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './campaign-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-overview.component.scss'],
})
export class CampaignOverviewComponent implements OnChanges {
  @Input() campaign!: Campaign;

  platformUrlRows: CampaignPlatformOverviewRow[] = [];
  /** Platforms exist but their URLs stay hidden until the landing page is created. */
  showLandingPagePendingHint = false;
  readonly landingPagePendingHint = LANDING_PAGE_PENDING_SHARE_LINKS_HINT;

  private messageService = inject(NotificationService);

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaign']) {
      const landingReady = isCampaignLandingPageReady(this.campaign);
      this.platformUrlRows = buildCampaignPlatformOverviewRows(
        this.campaign?.id ?? '',
        this.campaign?.platforms,
        landingReady
      );
      this.showLandingPagePendingHint =
        !landingReady && getCampaignPlatformIds(this.campaign?.platforms).length > 0;
    }
  }

  getPlatformDisplay(): string {
    return formatCampaignPlatformList(this.campaign?.platforms);
  }

  copyPlatformUrl(url: string, detail = 'Campaign URL copied to clipboard.'): void {
    if (!url?.trim()) {
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail,
        life: 2000,
      });
    });
  }

  trackByPlatformId(_: number, row: CampaignPlatformOverviewRow): string {
    return row.id;
  }

  getLocationTooltipText(): string {
    if (this.campaign.location_list && this.campaign.location_list.length > 0) {
      return this.campaign.location_list.map(location => `• ${location}`).join('\n');
    }
    return this.campaign.location;
  }

  getUserCount(): number {
    if (this.campaign.segments?.length) {
      const fromSegments = this.campaign.segments.reduce(
        (sum, segment) => sum + (Number(segment.customer_count) || 0),
        0
      );
      if (fromSegments > 0) {
        return fromSegments;
      }
    }
    return Number(this.campaign.total_customers) || 0;
  }

  /**
   * Formats an input date string into an "Month day, year" representation using en-US locale.
   * @example
   * ormatDate('2023-03-15')
   * March 15, 2023
   * @param {{string}} {{dateString}} - Input date string (ISO or any string parseable by Date).
   * @returns {{string}} Return formatted date like "Month day, year", or empty string if input is falsy.
   **/
  formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    return date.toLocaleDateString('en-US', options);
  }

  onEditClick(): void {
    this.router.navigate(['/campaign-editor', 'overview'], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  onViewAnalytics(): void {
    // Navigate to analytics page or open analytics modal
    this.router.navigate(['/dashboard'], {
      queryParams: { campaignId: this.campaign.id, tab: 'Campaign Management' },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
